import { OverlayView } from '@react-google-maps/api';
import { Package } from 'lucide-react';
import { MapLocationData } from '@hero/web-components';
import { useMapMarkerColors } from '@hero/web-hooks';
import { useMapContext } from '@hero/web-context';
import { MarkerCard } from './MarkerCard';

export interface PickupMarkerProps {
  /** Location data */
  location: MapLocationData;
  /** Callback when marker is clicked */
  onClick?: () => void;
  /** Whether this location is currently being edited */
  isEditing?: boolean;
  /** Optional distance information */
  distance?: {
    text: string;
    value: number;
  };
  /** Optional duration information */
  duration?: {
    text: string;
    value: number;
  };
}

/**
 * PickupMarker - Custom styled pickup marker with Package icon
 */
export function PickupMarker({
  location,
  onClick,
  isEditing = false,
  distance,
  duration,
}: PickupMarkerProps) {
  const { markerColors } = useMapMarkerColors();
  const ctx = useMapContext();
  
  // Determine if location is inaccurate and indicator is enabled
  const isInaccurate = location.isAccurateLocation === false && ctx.enableAccurateLocationIndicator;
  
  // Use colors from centralized hook
  const bgColor = isEditing
    ? markerColors.pending.fillColor
    : isInaccurate
    ? markerColors.notAccurateLocation.fillColor
    : markerColors.pickup.fillColor;
  const pulseBgColor = isEditing
    ? markerColors.pending.paluse
    : isInaccurate
    ? markerColors.notAccurateLocation.paluse
    : markerColors.pickup.paluse;
  const borderColor = isInaccurate ? markerColors.notAccurateLocation.fillColor : '#ffffff';
  
  return (
    <OverlayView position={location.cords} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
      <div className="relative" dir="ltr">
        {/* Custom Tooltip Box - Always visible */}
        <div className="absolute bottom-[45px] left-1/2 -translate-x-1/2 min-w-[180px] z-10 pointer-events-none">
          <MarkerCard
            location={location}
            index={0}
            isOrigin={true}
            isEditing={isEditing}
            distance={distance}
            duration={duration}
          />
          {/* Arrow pointing down */}
          <div className="w-3 h-3 bg-white rotate-45 absolute -bottom-1.5 left-1/2 -translate-x-1/2 shadow-lg" />
        </div>

        {/* Marker */}
        <div className="relative cursor-pointer" onClick={onClick}>
          {/* Outer pulse circle */}
          <div className="absolute -translate-x-1/2 -translate-y-1/2 animate-pulse pointer-events-none">
            <div
              className="w-[40px] h-[40px] rounded-full"
              style={{ backgroundColor: `${pulseBgColor}33` }}
            />
          </div>

          {/* Inner circle with icon */}
          <div className="absolute -translate-x-1/2 -translate-y-1/2">
            <div
              className="w-[24px] h-[24px] rounded-full border-2 shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
              style={{ backgroundColor: bgColor, borderColor }}
            >
              <Package className="w-3 h-3 text-white" />
            </div>
          </div>
        </div>
      </div>
    </OverlayView>
  );
}
